import {
  createContext,
  type MutableRefObject,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePortalAuth } from "../hooks/usePortalAuth";

export type UIVersionMode = "legacy" | "vnext";
type ResolutionMode = "local" | "qa" | "production";

export interface UIVersionResolutionInput {
  tenantId: string | null;
  runtimeMode?: ResolutionMode;
  rollbackEnabled?: boolean;
  allowlistTenants?: readonly string[];
  cohortPercent?: number;
}

export interface UIVersionResolution {
  uiVersion: UIVersionMode;
  reason: string;
  tenantId: string | null;
  runtimeMode: ResolutionMode;
}
type ResolvedUIVersion = UIVersionResolution & { rollbackEnabled: boolean };

export interface UIVersionContextValue extends Omit<UIVersionResolution, "reason"> {
  reason: string;
}

export const ROLLBACK_STORAGE_KEY = "portal_ui_version_rollback";
const ROLLBACK_ENV_KEY = "VITE_PORTAL_UI_VERSION_ROLLBACK";
const ALLOWLIST_ENV_KEY = "VITE_PORTAL_UI_VERSION_ALLOWLIST_TENANTS";
const COHORT_PERCENT_ENV_KEY = "VITE_PORTAL_UI_VERSION_COHORT_PERCENT";
const UI_VERSION_ATTR = "data-ui-version";
const UI_VERSION_ROLLBACK_ATTR = "data-ui-version-rollback";

const UIVersionContext = createContext<UIVersionContextValue | null>(null);

function normalizeTenantList(value?: string): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

function getRuntimeMode(hostname = ""): ResolutionMode {
  if (import.meta.env.DEV) return "local";
  const normalizedHost = hostname.toLowerCase();
  if (/[.]qa[.]|\.staging[.]|\.sandbox[.]/.test(normalizedHost) || normalizedHost.endsWith(".qa") || normalizedHost === "qa") {
    return "qa";
  }
  return "production";
}

function isRollbackFeatureEnabled(): boolean {
  const raw = import.meta.env[ROLLBACK_ENV_KEY];
  return raw === "1" || raw?.toLowerCase() === "true";
}

export function isRollbackEnabled(runtimeMode: ResolutionMode = getRuntimeMode()): boolean {
  if (isRollbackFeatureEnabled()) return true;
  if (runtimeMode === "production") return false;

  if (typeof window === "undefined") return false;

  try {
    const raw = window.localStorage.getItem(ROLLBACK_STORAGE_KEY);
    return raw === "1" || raw?.toLowerCase() === "true";
  } catch {
    return false;
  }
}

function isInCohort(tenantId: string, rawPercent = 0): boolean {
  const percent = Math.max(0, Math.min(100, Math.floor(Number(rawPercent))));
  if (!Number.isFinite(percent) || percent <= 0) return false;
  const normalizedTenant = tenantId.trim().toLowerCase();
  if (!normalizedTenant) return false;
  let hash = 0;
  for (let i = 0; i < normalizedTenant.length; i += 1) {
    hash = (hash * 31 + normalizedTenant.charCodeAt(i)) >>> 0;
  }
  return hash % 100 < percent;
}

function setAttributeValue(
  root: HTMLElement,
  attr: string,
  value: string | null,
  ownershipRef: MutableRefObject<string | null>,
) {
  if (value === null) {
    root.removeAttribute(attr);
  } else {
    root.setAttribute(attr, value);
  }
  ownershipRef.current = value;
}

function isOwnedByProvider(
  root: HTMLElement,
  attr: string,
  lastWritten: string | null,
): boolean {
  return root.getAttribute(attr) === lastWritten;
}

export function resolvePortalUIVersion(input: UIVersionResolutionInput): UIVersionResolution {
  const tenantId = input.tenantId ? input.tenantId.trim().toLowerCase() : null;
  const runtimeMode = input.runtimeMode ?? "production";
  const rollbackEnabled = input.rollbackEnabled ?? false;
  const cohortPercent = input.cohortPercent ?? 0;
  const allowlistTenants = (input.allowlistTenants ?? []).map((tenant) => tenant.trim().toLowerCase()).filter(Boolean);

  if (rollbackEnabled) return { uiVersion: "legacy", reason: "rollback-enabled", tenantId, runtimeMode };
  if (runtimeMode === "local") return { uiVersion: "vnext", reason: "local-mode", tenantId, runtimeMode };
  if (runtimeMode === "qa") return { uiVersion: "vnext", reason: "qa-mode", tenantId, runtimeMode };
  if (!tenantId) return { uiVersion: "legacy", reason: "missing-tenant", tenantId, runtimeMode };
  if (allowlistTenants.includes(tenantId)) {
    return { uiVersion: "vnext", reason: "tenant-allowlist", tenantId, runtimeMode };
  }
  if (isInCohort(tenantId, cohortPercent)) {
    return { uiVersion: "vnext", reason: "tenant-cohort", tenantId, runtimeMode };
  }
  return { uiVersion: "legacy", reason: "production-default", tenantId, runtimeMode };
}

export function UIVersionProvider({ children }: { children: ReactNode }) {
  const { tenantId: authTenantId } = usePortalAuth();
  const hostname = typeof window === "undefined" ? "" : window.location.hostname;
  const runtimeMode = getRuntimeMode(hostname);
  const [rollbackEnabled, setRollbackEnabled] = useState<boolean>(() => isRollbackEnabled(runtimeMode));
  const rootSnapshotRef = useRef<{ uiVersion: string | null; uiVersionRollback: string | null } | null>(null);
  const lastWrittenVersionRef = useRef<string | null>(null);
  const lastWrittenRollbackRef = useRef<string | null>(null);

  const tenantId = authTenantId?.trim().toLowerCase() || null;
  const env = import.meta.env as Record<string, string | undefined>;
  const allowlistRaw = env[ALLOWLIST_ENV_KEY];
  const allowlistTenants = useMemo(() => normalizeTenantList(allowlistRaw), [allowlistRaw]);
  const cohortPercent = Number(env[COHORT_PERCENT_ENV_KEY] ?? 0);

  const resolved = useMemo(
    () => {
      const resolvedValue = resolvePortalUIVersion({
        tenantId,
        runtimeMode,
        rollbackEnabled,
        allowlistTenants,
        cohortPercent,
      });
      return { ...resolvedValue, rollbackEnabled };
    },
    [tenantId, runtimeMode, rollbackEnabled, allowlistTenants, cohortPercent],
  ) as ResolvedUIVersion;

  useEffect(() => {
    const updateRollback = () => setRollbackEnabled(isRollbackEnabled(runtimeMode));
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === ROLLBACK_STORAGE_KEY) {
        updateRollback();
      }
    };

    updateRollback();
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, [runtimeMode]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (rootSnapshotRef.current === null) {
      rootSnapshotRef.current = {
        uiVersion: root.getAttribute(UI_VERSION_ATTR),
        uiVersionRollback: root.getAttribute(UI_VERSION_ROLLBACK_ATTR),
      };

      const initialRollbackValue = resolved.rollbackEnabled ? "1" : null;
      setAttributeValue(root, UI_VERSION_ATTR, resolved.uiVersion, lastWrittenVersionRef);
      setAttributeValue(root, UI_VERSION_ROLLBACK_ATTR, initialRollbackValue, lastWrittenRollbackRef);
      return;
    }

    if (isOwnedByProvider(root, UI_VERSION_ATTR, lastWrittenVersionRef.current)) {
      setAttributeValue(root, UI_VERSION_ATTR, resolved.uiVersion, lastWrittenVersionRef);
    }

    const resolvedRollbackValue = resolved.rollbackEnabled ? "1" : null;
    if (isOwnedByProvider(root, UI_VERSION_ROLLBACK_ATTR, lastWrittenRollbackRef.current)) {
      setAttributeValue(
        root,
        UI_VERSION_ROLLBACK_ATTR,
        resolvedRollbackValue,
        lastWrittenRollbackRef,
      );
    }
  }, [resolved.uiVersion, resolved.rollbackEnabled]);

  useLayoutEffect(() => {
    return () => {
      if (!rootSnapshotRef.current) return;

      const root = document.documentElement;
      const snapshot = rootSnapshotRef.current;
      if (root.getAttribute(UI_VERSION_ATTR) === lastWrittenVersionRef.current) {
        if (snapshot.uiVersion === null) {
          root.removeAttribute(UI_VERSION_ATTR);
        } else {
          root.setAttribute(UI_VERSION_ATTR, snapshot.uiVersion);
        }
      }

      if (root.getAttribute(UI_VERSION_ROLLBACK_ATTR) === lastWrittenRollbackRef.current) {
        if (snapshot.uiVersionRollback === null) {
          root.removeAttribute(UI_VERSION_ROLLBACK_ATTR);
        } else {
          root.setAttribute(UI_VERSION_ROLLBACK_ATTR, snapshot.uiVersionRollback);
        }
      }

      rootSnapshotRef.current = null;
      lastWrittenVersionRef.current = null;
      lastWrittenRollbackRef.current = null;
    };
  }, []);

  return <UIVersionContext.Provider value={resolved}>{children}</UIVersionContext.Provider>;
}

export function useUIVersion() {
  const ctx = useContext(UIVersionContext);
  if (!ctx) throw new Error("useUIVersion must be used inside UIVersionProvider");
  return ctx;
}
